import { useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

type EventType =
  | 'page_view'
  | 'upload'
  | 'compress'
  | 'download'
  | 'view_pricing'
  | 'click_subscribe'
  | 'reach_limit'
  | 'register';

function getSessionId(): string {
  let id = sessionStorage.getItem('analytics_session_id');
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem('analytics_session_id', id);
  }
  return id;
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
  };
}

export function useAnalytics() {
  const sessionRef = useRef<{
    sessionId: string;
    referrer: string | undefined;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    device_type: string;
  } | null>(null);

  // Initialize session data once
  if (!sessionRef.current) {
    const utm = getUtmParams();
    sessionRef.current = {
      sessionId: getSessionId(),
      referrer: document.referrer || undefined,
      ...utm,
      device_type: getDeviceType(),
    };
  }

  const trackEvent = useCallback(
    (eventType: EventType, eventData?: Record<string, unknown>) => {
      // No trackear en la página de analytics
      if (window.location.pathname === '/analytics') return;

      // No trackear en entorno Lovable
      const host = window.location.hostname;
      if (host.includes('lovable.app') || host.includes('lovable.dev')) return;

      const s = sessionRef.current!;
      // Fire-and-forget
      supabase.functions
        .invoke('track-event', {
          body: {
            session_id: s.sessionId,
            event_type: eventType,
            event_data: eventData || {},
            referrer: s.referrer,
            utm_source: s.utm_source,
            utm_medium: s.utm_medium,
            utm_campaign: s.utm_campaign,
            device_type: s.device_type,
          },
        })
        .catch(() => {
          // Silent fail - analytics should never break the app
        });
    },
    []
  );

  const trackPageView = useCallback(
    (page: string) => {
      trackEvent('page_view', { page });
    },
    [trackEvent]
  );

  return { trackEvent, trackPageView };
}
